import { message } from 'antd';
import { createDefaultSlideStructure } from './layoutStructure';

/**
 * Local presentations store.
 *
 * Presentations created offline live entirely in the browser under a single
 * localStorage key. They have no server counterpart until the user explicitly
 * syncs them, so every read/write of the raw key goes through this module and
 * no other file has to know the storage shape.
 */

const LOCAL_PRESENTATION_KEY = 'local_presentation_slide';

/** Maximum number of local presentations a user may keep at once. */
export const MAX_LOCAL_PRESENTATIONS = 5;

/**
 * A new local presentation starts with one slide.
 *
 * Server-backed presentations get their first slide from `/api/create`; offline
 * there is no such call, so an empty `slides` map would open the editor with
 * nothing to edit.
 */
const INITIAL_CONTENT = () => {
  const slideId = `slide${generateId()}`;
  return {
    slides: {
      [slideId]: createDefaultSlideStructure(
        `default-newSlidelayoutTitle-${generateId()}`,
        `default-newSlidelayoutText-${generateId()}`
      ),
    },
    mirrorSlides: {},
    currentSlideId: slideId,
  };
};

// Read the whole map defensively — corrupt/absent storage falls back to {}.
function readStore() {
  try {
    const raw = localStorage.getItem(LOCAL_PRESENTATION_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('Error reading local presentations:', error);
    return {};
  }
}

// Slides carry a base64 thumbnail per slide, so a write can genuinely exceed
// the quota. Tell the user rather than silently losing their work.
function writeStore(store) {
  try {
    localStorage.setItem(LOCAL_PRESENTATION_KEY, JSON.stringify(store));
    return true;
  } catch (error) {
    console.error('Error writing local presentations:', error);
    const isQuotaError =
      error && (error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014);
    if (isQuotaError) {
      message.error('Not enough local storage to save this presentation');
    }
    return false;
  }
}

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** All local presentations, newest `updateDate` first. */
export function getLocalPresentations() {
  const store = readStore();
  return Object.values(store).sort(
    (a, b) => new Date(b?.updateDate || 0) - new Date(a?.updateDate || 0)
  );
}

/** A single local presentation record, or null. */
export function getLocalPresentation(id) {
  if (!id) return null;
  return readStore()[id] || null;
}

/** How many local presentations are currently stored. */
export function getLocalPresentationCount() {
  return Object.keys(readStore()).length;
}

/** True when the store is already at MAX_LOCAL_PRESENTATIONS. */
export function isLocalPresentationLimitReached() {
  return getLocalPresentationCount() >= MAX_LOCAL_PRESENTATIONS;
}

/**
 * Create a new local presentation with a fresh UUID and empty content.
 * Returns the created record, or null when the limit is already reached.
 */
export function createLocalPresentation({ name, description = '', creator = null } = {}) {
  const store = readStore();
  if (Object.keys(store).length >= MAX_LOCAL_PRESENTATIONS) {
    return null;
  }
  const now = new Date().toISOString();
  const record = {
    id: generateId(),
    name: (name || '').trim() || 'Untitled presentation',
    description: description || '',
    documentType: 7,
    isLocal: true,
    creator: creator || null,
    createDate: now,
    updateDate: now,
    content: INITIAL_CONTENT(),
  };
  store[record.id] = record;
  writeStore(store);
  return record;
}

/** Replace a local presentation's content and bump its updateDate. */
export function updateLocalPresentationContent(id, content) {
  const store = readStore();
  const record = store[id];
  if (!record) return null;
  record.content = {
    slides: content?.slides ?? record.content?.slides ?? {},
    mirrorSlides: content?.mirrorSlides ?? record.content?.mirrorSlides ?? {},
    currentSlideId: content?.currentSlideId ?? record.content?.currentSlideId ?? '',
  };
  record.updateDate = new Date().toISOString();
  store[id] = record;
  writeStore(store);
  return record;
}

/** Rename a local presentation and bump its updateDate. */
export function renameLocalPresentation(id, name) {
  const store = readStore();
  const record = store[id];
  if (!record) return null;
  record.name = (name || '').trim() || record.name;
  record.updateDate = new Date().toISOString();
  store[id] = record;
  writeStore(store);
  return record;
}

/** Permanently remove a local presentation from storage. */
export function removeLocalPresentation(id) {
  const store = readStore();
  if (store[id]) {
    delete store[id];
    writeStore(store);
  }
}

/**
 * True when the current URL is a local presentation (`?local=true`).
 * Read straight from the URL so non-React modules (API + socket guards) can
 * call it synchronously.
 */
export function isLocalPresentationRoute() {
  try {
    return new URLSearchParams(window.location.search).get('local') === 'true';
  } catch (error) {
    return false;
  }
}
